// frontend/src/components/shared/modals/EnrollmentSettingsModal.tsx
import { useState, useEffect, type FormEvent } from 'react';
import { X, Calendar, GraduationCap, AlertCircle } from 'lucide-react';
import { userService } from '../../../services/userService';
import { ModalShell } from '../../ui';
import EnrollmentUpdateWarningModal, {
  type EnrollmentUpdateOption,
} from './EnrollmentUpdateWarningModal';
import type { UserEnrollment } from '@/types';

type SemesterCode = 'spring' | 'summer' | 'fall' | 'winter';

interface SemesterOption {
  value: string;
  label: string;
  isPast?: boolean;
  isFuture?: boolean;
}

interface PendingEnrollmentData {
  startSemester: string;
  gradSemester: string;
}

export interface EnrollmentSaveResult extends UserEnrollment {
  cleared?: boolean;
  [key: string]: unknown;
}

export interface EnrollmentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEnrollment?: string | null;
  expectedGraduation?: string | null;
  userId?: number | null;
  onSave?: (result: EnrollmentSaveResult) => Promise<void> | void;
  onResetPlan?: (newStartSemester: string) => Promise<void> | void;
}

const EnrollmentSettingsModal = ({
  isOpen,
  onClose,
  currentEnrollment,
  expectedGraduation,
  userId,
  onSave,
  onResetPlan,
}: EnrollmentSettingsModalProps) => {
  const [startSemester, setStartSemester] = useState(currentEnrollment || '');
  const [gradSemester, setGradSemester] = useState(expectedGraduation || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const [pendingData, setPendingData] = useState<PendingEnrollmentData | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStartSemester(currentEnrollment || '');
      setGradSemester(expectedGraduation || '');
      setError(null);
      setShowClearConfirm(false);
    }
  }, [isOpen, currentEnrollment, expectedGraduation]);

  const getSemesterOrder = (semester: string): number => {
    // W50 item 15: within a NOMINAL year, winter (Dec prior/Jan-Mar) comes
    // FIRST — the old map ranked it last, contradicting the canonical
    // encoding and misclassifying winter options as future.
    const order: Record<string, number> = { winter: 0, spring: 1, summer: 2, fall: 3 };
    return order[semester] ?? -1;
  };

  const capitalizeFirst = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

  const generateSemesterOptions = (): SemesterOption[] => {
    const options: SemesterOption[] = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const currentSemesterYear = currentYear;
    let currentSemesterType: SemesterCode = 'fall';
    if (currentMonth >= 1 && currentMonth <= 5) {
      currentSemesterType = 'spring';
    } else if (currentMonth >= 6 && currentMonth <= 7) {
      currentSemesterType = 'summer';
    }

    for (let year = currentYear - 5; year <= currentYear + 8; year++) {
      const semesters: SemesterCode[] = ['spring', 'summer', 'fall', 'winter'];

      semesters.forEach((sem) => {
        const displayYear = sem === 'winter' ? year + 1 : year;
        // W50 item 15: value carries the NOMINAL year (same as the label).
        // The old `${sem}-${year}` made a "Winter 2027" label store
        // winter-2026, which the canonical parser (post-W50) reads as
        // Winter 2026 — a two-year skew between what the user picked and
        // what the plan recorded.
        const value = `${sem}-${displayYear}`;

        // Compare in NOMINAL-year space — the same space getSemesterOrder
        // ranks in (winter first). Comparing the LOOP year against the
        // current term while ranking winter first made "Winter <next>"
        // (Dec this year) pass the future-exclusion and read as PAST every
        // September–December at every institution (PR #306 review).
        if (
          displayYear > currentSemesterYear ||
          (displayYear === currentSemesterYear &&
            getSemesterOrder(sem) > getSemesterOrder(currentSemesterType))
        ) {
          return;
        }

        options.push({
          value,
          label: `${capitalizeFirst(sem)} ${displayYear}`,
          isPast:
            displayYear < currentSemesterYear ||
            (displayYear === currentSemesterYear &&
              getSemesterOrder(sem) < getSemesterOrder(currentSemesterType)),
          isFuture: false,
        });
      });
    }

    return options;
  };

  const generateGraduationOptions = (): SemesterOption[] => {
    const options: SemesterOption[] = [];
    const currentYear = new Date().getFullYear();

    for (let year = currentYear; year <= currentYear + 8; year++) {
      const semesters: SemesterCode[] = ['spring', 'summer', 'fall', 'winter'];

      semesters.forEach((sem) => {
        const displayYear = sem === 'winter' ? year + 1 : year;
        // W50 item 15: value carries the NOMINAL year (same as the label).
        // The old `${sem}-${year}` made a "Winter 2027" label store
        // winter-2026, which the canonical parser (post-W50) reads as
        // Winter 2026 — a two-year skew between what the user picked and
        // what the plan recorded.
        const value = `${sem}-${displayYear}`;

        options.push({
          value,
          label: `${capitalizeFirst(sem)} ${displayYear}`,
        });
      });
    }

    return options;
  };

  const validateDates = (): boolean => {
    if (!startSemester || !gradSemester) return true;

    const [startSem, startYear] = startSemester.split('-');
    const [gradSem, gradYear] = gradSemester.split('-');

    if (parseInt(gradYear) < parseInt(startYear)) {
      return false;
    }

    if (
      parseInt(gradYear) === parseInt(startYear) &&
      getSemesterOrder(gradSem) <= getSemesterOrder(startSem)
    ) {
      return false;
    }

    return true;
  };

  const executeUpdate = async (updateType: EnrollmentUpdateOption): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const result = await userService.updateEnrollment(
        userId,
        pendingData?.startSemester || startSemester,
        pendingData?.gradSemester || gradSemester
      );

      if (updateType === 'reset-plan' && onResetPlan) {
        await onResetPlan(pendingData?.startSemester || startSemester);
      } else if (onSave) {
        await onSave(result as EnrollmentSaveResult);
      }

      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to update enrollment settings');
    } finally {
      setLoading(false);
      setShowWarning(false);
      setPendingData(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateDates()) {
      setError('Expected graduation must be after enrollment start');
      return;
    }

    if (!userId) {
      setError('User information not available. Please refresh and try again.');
      return;
    }

    if (startSemester !== currentEnrollment) {
      setPendingData({ startSemester, gradSemester });
      setShowWarning(true);
    } else {
      await executeUpdate('profile-only');
    }
  };

  const handleClearTimeline = async (): Promise<void> => {
    if (!userId) {
      setError('User information not available. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await userService.updateEnrollment(userId, null, null);

      if (onSave) {
        await onSave({ ...result, cleared: true });
      }

      onClose();
      setShowClearConfirm(false);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to clear timeline settings');
    } finally {
      setLoading(false);
    }
  };

  const handleWarningOption = async (option: EnrollmentUpdateOption): Promise<void> => {
    if (option === 'profile-only' || option === 'reset-plan') {
      await executeUpdate(option);
    } else {
      setShowWarning(false);
      setPendingData(null);
    }
  };

  if (!isOpen) return null;

  const enrollmentOptions = generateSemesterOptions();
  const graduationOptions = generateGraduationOptions();

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={onClose}
        ariaLabel="Enrollment settings"
        maxWidth="max-w-md"
      >
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-2">
              <GraduationCap className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold text-gray-900">
                Academic Enrollment Settings
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              {error && (
                <div className="flex items-center space-x-2 text-status-critical-fg bg-status-critical/10 p-3 rounded-card">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <div>
                <label
                  htmlFor="start-semester"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>Enrollment Start Semester</span>
                  </div>
                </label>
                <select
                  id="start-semester"
                  value={startSemester}
                  onChange={(e) => setStartSemester(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                  disabled={loading}
                >
                  <option value="">Select start semester</option>
                  {enrollmentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="grad-semester"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  <div className="flex items-center space-x-1">
                    <GraduationCap className="h-4 w-4" />
                    <span>Expected Graduation</span>
                  </div>
                </label>
                <select
                  id="grad-semester"
                  value={gradSemester}
                  onChange={(e) => setGradSemester(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                  disabled={loading}
                >
                  <option value="">Select graduation semester</option>
                  {graduationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                <p>
                  These dates help track your academic progress and can be updated anytime.
                </p>
              </div>
            </div>

            {(currentEnrollment || expectedGraduation) && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600 mb-3">
                  <p className="font-medium text-gray-900">Reset Timeline</p>
                  <p>
                    Clear your enrollment and graduation dates to reset your academic timeline.
                  </p>
                </div>

                {!showClearConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowClearConfirm(true)}
                    disabled={loading}
                    className="px-3 py-2 text-sm font-medium text-status-critical bg-surface-card border border-status-critical/40 rounded-control hover:bg-status-critical/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    Clear Timeline
                  </button>
                ) : (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-700">
                      Are you sure? This will reset your timeline.
                    </span>
                    <button
                      type="button"
                      onClick={handleClearTimeline}
                      disabled={loading}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-status-critical-strong rounded-control hover:bg-status-critical-strong/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {loading ? 'Clearing...' : 'Confirm Clear'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowClearConfirm(false)}
                      disabled={loading}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end space-x-2 px-6 py-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !startSemester || !gradSemester}
                className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
      </ModalShell>

      <EnrollmentUpdateWarningModal
        isOpen={showWarning}
        onClose={() => {
          setShowWarning(false);
          setPendingData(null);
        }}
        currentEnrollment={currentEnrollment}
        newEnrollment={pendingData?.startSemester}
        onOptionSelect={handleWarningOption}
      />
    </>
  );
};

export default EnrollmentSettingsModal;
